import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNav';
import CryptoIcon from '../components/CryptoIcon';
import MiniChart from '../components/MiniChart';
import { 
  Search, 
  TrendingUp, 
  TrendingDown, 
  Star,
  StarOff,
  RefreshCw,
  Filter,
  X,
  BarChart3,
  Activity,
  DollarSign,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Eye,
  EyeOff,
  Sparkles
} from 'lucide-react';
import { useBalanceVisibility } from '../contexts/BalanceVisibilityContext';
import { db, isFirebaseAvailable } from '../services/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

const Market = () => {
  const navigate = useNavigate();
  const { balanceVisible, toggleBalanceVisibility } = useBalanceVisibility();
  const [marketData, setMarketData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all', 'favorites', 'gainers', 'losers'
  const [sortBy, setSortBy] = useState('market_cap'); // 'market_cap', 'volume', 'price', 'change', 'name'
  const [favorites, setFavorites] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [savingFavorites, setSavingFavorites] = useState(false);
  const updateIntervalRef = useRef(null);

  const BACKEND_URL = '';

  useEffect(() => {
    fetchMarketData();
    
    // Auto-refresh every 30 seconds
    updateIntervalRef.current = setInterval(() => {
      fetchMarketData(true); // Silent refresh
    }, 30000);

    return () => {
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Load favorites from Firebase or localStorage
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // Try Firebase first
      if (isFirebaseAvailable()) {
        const userEmail = JSON.parse(atob(token.split('.')[1])).email;
        const userDocRef = doc(db, 'users', userEmail);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.market_favorites) {
            setFavorites(userData.market_favorites);
            return;
          }
        }
      }

      // Fallback to localStorage
      const saved = localStorage.getItem('market_favorites');
      if (saved) {
        setFavorites(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading favorites:', e);
      // Fallback to localStorage
      const saved = localStorage.getItem('market_favorites');
      if (saved) {
        try {
          setFavorites(JSON.parse(saved));
        } catch (err) {
          console.error('localStorage parse error:', err);
        }
      }
    }
  };

  useEffect(() => {
    applyFilters();
  }, [marketData, searchQuery, activeTab, sortBy, favorites]);

  const fetchMarketData = async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/market/prices?type=all`);
      if (response.ok) {
        const data = await response.json();
        setMarketData(data);
      }
    } catch (error) {
      console.error('Error fetching market data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...marketData];

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Tab filter
    if (activeTab === 'favorites') {
      filtered = filtered.filter(item => favorites.includes(item.symbol));
    } else if (activeTab === 'gainers') {
      filtered = filtered.filter(item => item.change > 0);
    } else if (activeTab === 'losers') {
      filtered = filtered.filter(item => item.change < 0);
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'price') return b.price - a.price;
      if (sortBy === 'change') return Math.abs(b.change) - Math.abs(a.change);
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'volume') return (b.volume_24h || 0) - (a.volume_24h || 0);
      if (sortBy === 'market_cap') return (b.market_cap || 0) - (a.market_cap || 0);
      return 0;
    });

    setFilteredData(filtered);
  };

  const toggleFavorite = async (symbol) => {
    const newFavorites = favorites.includes(symbol)
      ? favorites.filter(s => s !== symbol)
      : [...favorites, symbol];
    
    setFavorites(newFavorites);
    
    // Save to localStorage immediately
    localStorage.setItem('market_favorites', JSON.stringify(newFavorites));
    
    // Save to Firebase in background
    try {
      setSavingFavorites(true);
      const token = localStorage.getItem('token');
      if (token && isFirebaseAvailable()) {
        const userEmail = JSON.parse(atob(token.split('.')[1])).email;
        const userDocRef = doc(db, 'users', userEmail);
        await setDoc(userDocRef, { market_favorites: newFavorites }, { merge: true });
      }
    } catch (error) {
      console.error('Error saving favorites to Firebase:', error);
    } finally {
      setSavingFavorites(false);
    }
  };

  const getTopMovers = () => {
    const sorted = [...marketData].sort((a, b) => Math.abs(b.change) - Math.abs(a.change));
    return sorted.slice(0, 3);
  };

  const getMarketStats = () => {
    const totalMarketCap = marketData.reduce((sum, item) => sum + (item.market_cap || 0), 0);
    const totalVolume = marketData.reduce((sum, item) => sum + (item.volume_24h || 0), 0);
    const gainers = marketData.filter(item => item.change > 0).length;
    const losers = marketData.filter(item => item.change < 0).length;
    const avgChange = marketData.reduce((sum, item) => sum + item.change, 0) / marketData.length;

    return { totalMarketCap, totalVolume, gainers, losers, avgChange: avgChange || 0 };
  };

  const formatLargeNumber = (num) => {
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const stats = getMarketStats();
  const topMovers = getTopMovers();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading market data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800/50 to-blue-900/50 backdrop-blur-sm border-b border-cyan-500/20">
        <div className="px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">
              Crypto Market
            </h1>
            <button
              onClick={toggleBalanceVisibility}
              className="p-2 rounded-lg bg-slate-800/50 text-cyan-400 hover:bg-slate-700/50 transition-colors"
            >
              {balanceVisible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
            </button>
          </div>

          {/* Market Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gradient-to-br from-cyan-900/30 to-blue-900/30 backdrop-blur-sm rounded-lg p-3 border border-cyan-500/30">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-gray-400">Total Market Cap</span>
              </div>
              <p className="text-lg font-bold text-cyan-400">{formatLargeNumber(stats.totalMarketCap)}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 backdrop-blur-sm rounded-lg p-3 border border-purple-500/30">
              <div className="flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-gray-400">24h Volume</span>
              </div>
              <p className="text-lg font-bold text-purple-400">{formatLargeNumber(stats.totalVolume)}</p>
            </div>
            <div className="bg-gradient-to-br from-green-900/30 to-blue-900/30 backdrop-blur-sm rounded-lg p-3 border border-green-500/30">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-xs text-gray-400">Gainers</span>
              </div>
              <p className="text-lg font-bold text-green-400">{stats.gainers} / {marketData.length}</p>
            </div>
            <div className="bg-gradient-to-br from-red-900/30 to-blue-900/30 backdrop-blur-sm rounded-lg p-3 border border-red-500/30">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-red-400" />
                <span className="text-xs text-gray-400">Losers</span>
              </div>
              <p className="text-lg font-bold text-red-400">{stats.losers} / {marketData.length}</p>
            </div>
          </div>

          {/* Top Movers Carousel */}
          {topMovers.length > 0 && (
            <div className="overflow-x-auto hide-scrollbar mb-4">
              <div className="flex gap-3">
                {topMovers.map((item, idx) => (
                  <div
                    key={idx}
                    className="min-w-[200px] bg-gradient-to-br from-slate-800/50 to-blue-900/30 rounded-lg p-3 border border-cyan-500/20"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CryptoIcon symbol={item.symbol.split('/')[0]} size="sm" />
                      <span className="font-semibold text-white text-sm">{item.symbol.split('/')[0]}</span>
                      <span className={`ml-auto text-xs font-bold ${
                        item.change >= 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
                      </span>
                    </div>
                    <p className="text-white font-bold">${item.price.toLocaleString()}</p>
                    <p className="text-xs text-gray-400">{item.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search cryptocurrencies..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-cyan-500/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar">
            {[
              { id: 'all', label: 'All', count: marketData.length },
              { id: 'favorites', label: 'Favorites', count: favorites.length },
              { id: 'gainers', label: 'Gainers', count: stats.gainers },
              { id: 'losers', label: 'Losers', count: stats.losers }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg shadow-cyan-500/30'
                    : 'bg-slate-800/30 text-gray-400 hover:bg-slate-700/50'
                }`}
              >
                {tab.label} {tab.count > 0 && `(${tab.count})`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="px-4 py-3 flex items-center justify-between bg-slate-800/30 border-b border-cyan-500/10">
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchMarketData()}
            className="p-2 rounded-lg bg-slate-800/50 text-cyan-400 hover:bg-slate-700/50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-400">{filteredData.length} assets</span>
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 bg-slate-800/50 border border-cyan-500/20 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
        >
          <option value="market_cap">Market Cap</option>
          <option value="volume">Volume</option>
          <option value="price">Price</option>
          <option value="change">Change</option>
          <option value="name">Name</option>
        </select>
      </div>

      {/* Market List */}
      <div className="px-4 py-4">
        {filteredData.length === 0 ? (
          <div className="text-center py-12">
            <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No assets found</p>
            <p className="text-gray-500 text-sm mt-2">
              {searchQuery ? 'Try a different search term' : 'Add some favorites to see them here'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredData.map((item, index) => (
              <div
                key={item.symbol}
                onClick={() => navigate('/binary-trade', { state: { selectedPair: item.symbol } })}
                className="bg-gradient-to-r from-slate-800/40 to-blue-900/20 backdrop-blur-sm rounded-xl p-4 border border-cyan-500/10 hover:border-cyan-500/30 hover:shadow-lg hover:shadow-cyan-500/10 transition-all cursor-pointer group"
                style={{
                  animationDelay: `${index * 0.05}s`,
                  animation: 'slideUp 0.3s ease-out'
                }}
              >
                {/* Header Row: Icon, Name, Favorite, Price */}
                <div className="flex items-center justify-between mb-3">
                  {/* Left: Icon + Name + Change */}
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">
                      <CryptoIcon symbol={item.symbol.split('/')[0]} size="lg" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-white text-base leading-tight">{item.symbol.split('/')[0]}</h3>
                      <p className="text-xs text-gray-400 truncate">{item.name}</p>
                    </div>
                  </div>

                  {/* Right: Favorite + Price + Change */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {/* Favorite Button - Larger touch target */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(item.symbol);
                      }}
                      className="p-2 rounded-lg hover:bg-slate-700/50 transition-colors"
                      disabled={savingFavorites}
                    >
                      {favorites.includes(item.symbol) ? (
                        <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                      ) : (
                        <StarOff className="w-5 h-5 text-gray-500 group-hover:text-gray-400" />
                      )}
                    </button>

                    {/* Price + Change Vertical */}
                    <div className="text-right">
                      <p className="text-white font-bold text-base leading-tight mb-1">
                        ${item.price >= 1 ? item.price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) : item.price.toFixed(6)}
                      </p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${
                        item.change >= 0 
                          ? 'bg-green-500/20 text-green-400' 
                          : 'bg-red-500/20 text-red-400'
                      }`}>
                        {item.change >= 0 ? (
                          <ArrowUpRight className="w-3 h-3" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3" />
                        )}
                        {item.change >= 0 ? '+' : ''}{item.change.toFixed(2)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Mini Chart */}
                {item.price_history && item.price_history.length > 0 && (
                  <div className="mb-3 opacity-70 group-hover:opacity-100 transition-opacity">
                    <MiniChart 
                      data={item.price_history} 
                      isPositive={item.change >= 0}
                      width={280}
                      height={40}
                    />
                  </div>
                )}

                {/* Footer Row: Market Cap + Volume - Better spacing on mobile */}
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-cyan-500/10">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Market Cap</p>
                    <p className="text-sm text-cyan-400 font-semibold">{formatLargeNumber(item.market_cap || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Volume 24h</p>
                    <p className="text-sm text-purple-400 font-semibold">{formatLargeNumber(item.volume_24h || 0)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNav />

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Market;